import { getAuthHeader } from "./auth";

function resolveApiBase() {
  if (process.env.NEXT_PUBLIC_API_BASE_URL) {
    return process.env.NEXT_PUBLIC_API_BASE_URL;
  }

  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:4000/api";
    }
    return "/api";
  }

  return "http://localhost:4000/api";
}

export const API_BASE = resolveApiBase().replace(/\/$/, "");
// API_BASE above has "/api" appended; this is the bare origin, used for
// non-/api endpoints (socket.io, raw job-location polling) that need the
// same smart localhost-vs-production fallback without the "/api" suffix.
export const API_ORIGIN = API_BASE.replace(/\/api$/, "");

function resolveSocketBase() {
  if (process.env.NEXT_PUBLIC_SOCKET_URL) {
    return process.env.NEXT_PUBLIC_SOCKET_URL;
  }
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:4000";
    }
    // In production nginx proxies /socket.io/ on the same origin - connecting
    // to a hardcoded localhost:4000 here would just hit the visitor's own
    // machine and silently never work.
    return window.location.origin;
  }
  return "http://localhost:4000";
}

export const SOCKET_BASE = resolveSocketBase();

async function request<T>(path: string, init?: RequestInit, auth = false): Promise<T> {
  const authHeaders = auth ? getAuthHeader() : {};
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
      ...authHeaders,
    },
    cache: "no-store",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || err.error || `Request failed: ${res.status}`);
  }
  return res.json();
}

// Authenticated request helper
function authReq<T>(path: string, init?: RequestInit): Promise<T> {
  return request<T>(path, init, true);
}

export const api = {
  // ── Auth ──
  register: (body: { email: string; password: string; name: string; phone?: string }) =>
    request("/auth/register", { method: "POST", body: JSON.stringify(body) }),
  login: (body: { email: string; password: string }) =>
    request("/auth/login", { method: "POST", body: JSON.stringify(body) }),
  me: () => authReq("/auth/me"),
  updateMe: (body: { name?: string; phone?: string }) =>
    authReq("/auth/me", { method: "PATCH", body: JSON.stringify(body) }),
  updateCookieConsent: (consent: { essential: boolean; analytics: boolean; marketing: boolean }) =>
    authReq("/auth/cookie-consent", { method: "POST", body: JSON.stringify({ consent }) }),

  // ── Contractor Auth ──
  registerContractor: (body: any) =>
    request("/contractor/register", { method: "POST", body: JSON.stringify(body) }),
  loginContractor: (body: { email: string; password: string }) =>
    request("/contractor/login", { method: "POST", body: JSON.stringify(body) }),
  requestContractorClaim: (email: string) =>
    request("/contractor/claim/request", { method: "POST", body: JSON.stringify({ email }) }),
  verifyContractorClaim: (email: string, code: string) =>
    request("/contractor/claim/verify", { method: "POST", body: JSON.stringify({ email, code }) }),
  getContractorMe: () => authReq("/contractor/me"),
  updateContractorMe: (body: any) =>
    authReq("/contractor/me", { method: "PATCH", body: JSON.stringify(body) }),
  requestVerification: (body: { type: "email" | "phone"; target: string }) =>
    request("/contractor/verify/request", { method: "POST", body: JSON.stringify(body) }),
  submitVerification: (body: { type: "email" | "phone"; target: string; code: string; id?: string }) =>
    request("/contractor/verify/submit", { method: "POST", body: JSON.stringify(body) }),


  // ── Pricing ──
  estimatePricing: (body: { description: string; location: string; postcode?: string; urgency: string }) =>
    request("/pricing/estimate", { method: "POST", body: JSON.stringify(body) }),

  // ── Jobs ──
  createJob: (body: { description: string; location: string; address?: string; postcode?: string; urgency: string; customerLocation?: any; images?: string[]; videos?: string[] }) =>
    authReq("/jobs", { method: "POST", body: JSON.stringify(body) }),

  getMyJobs: () => authReq("/jobs/mine"),           // authenticated — own jobs only
  getJobs: (params?: { postcode?: string; contractorId?: string; scope?: "local" | "all" }) => {
    const q = new URLSearchParams();
    if (params?.postcode) q.append("postcode", params.postcode);
    if (params?.contractorId) q.append("contractorId", params.contractorId);
    if (params?.scope) q.append("scope", params.scope);
    const query = q.toString() ? `?${q.toString()}` : "";
    return request(`/jobs${query}`);                 // unauthenticated — all jobs (contractor feed)
  },
  getJob: (id: string) => authReq(`/jobs/${id}`),
  getJobBids: (jobId: string) => request(`/jobs/${jobId}/bids`),
  submitJobBid: (jobId: string, body: { contractorId?: string; amount: number; estimatedHours?: number | string; message?: string; includesMaterials?: boolean; materialsNote?: string }) =>
    authReq(`/jobs/${jobId}/bids`, { method: "POST", body: JSON.stringify(body) }),
  selectJobBid: (jobId: string, bidId: string) =>
    authReq(`/jobs/${jobId}/bids/${bidId}/select`, { method: "POST", body: JSON.stringify({}) }),
  repostJob: (jobId: string) =>
    authReq(`/jobs/${jobId}/repost`, { method: "POST", body: JSON.stringify({}) }),
  deleteJob: (jobId: string) =>
    authReq(`/jobs/${jobId}`, { method: "DELETE" }),
  updateJob: (jobId: string, body: { description?: string; images?: string[]; videos?: string[] }) =>
    authReq(`/jobs/${jobId}`, { method: "PATCH", body: JSON.stringify(body) }),
  getConversations: () => authReq("/conversations"),
  getAdminConversations: () => authReq("/admin/conversations"),
  getConversationMessages: (conversationId: string) =>
    authReq(`/conversations/${conversationId}/messages`),
  sendConversationMessage: (conversationId: string, body: { body?: string; attachments?: string[] }) =>
    authReq(`/conversations/${conversationId}/messages`, { method: "POST", body: JSON.stringify(body) }),
  markConversationRead: (conversationId: string) =>
    authReq(`/conversations/${conversationId}/read`, { method: "POST", body: JSON.stringify({}) }),
  updateContactConsent: (conversationId: string, consent: boolean) =>
    authReq(`/conversations/${conversationId}/contact-consent`, { method: "POST", body: JSON.stringify({ consent }) }),
  startMaskedCall: (conversationId: string) =>
    authReq(`/conversations/${conversationId}/call`, { method: "POST", body: JSON.stringify({}) }),
  acceptJob: (id: string, contractorId: string) =>
    authReq(`/jobs/${id}/accept`, { method: "POST", body: JSON.stringify({ contractorId }) }),
  completeJob: (id: string) =>
    authReq(`/jobs/${id}/complete`, { method: "POST", body: JSON.stringify({}) }),
  confirmCompletion: (id: string) =>
    authReq(`/jobs/${id}/confirm-completion`, { method: "POST", body: JSON.stringify({}) }),
  reviewJob: (id: string, rating: number, comment: string) =>
    authReq(`/jobs/${id}/review`, { method: "POST", body: JSON.stringify({ rating, comment }) }),

  // ── Job Parts ──
  addJobPart: (jobId: string, body: { name: string; price: number }) =>
    authReq(`/jobs/${jobId}/parts`, { method: "POST", body: JSON.stringify(body) }),
  approveJobPart: (jobId: string, partId: string) =>
    authReq(`/jobs/${jobId}/parts/${partId}/approve`, { method: "PATCH" }),
  rejectJobPart: (jobId: string, partId: string) =>
    authReq(`/jobs/${jobId}/parts/${partId}/reject`, { method: "PATCH" }),


  // ── Contractors ──
  getContractors: () => request("/contractors"),
  getContractorReviews: (id: string) => request(`/contractors/${id}/reviews`),
  getContractor: (id: string) => request(`/contractors/${id}`),
  createContractor: (body: unknown) =>
    request("/contractors", { method: "POST", body: JSON.stringify(body) }),
  updateContractor: (id: string, body: unknown) =>
    authReq(`/contractors/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  getTrades: () => request("/contractors/trades"),

  // ── Admin ──
  loginAdmin: (passcode: string) =>
    request("/admin/login", { method: "POST", body: JSON.stringify({ passcode }) }),
  getAdminJobs: () => authReq("/admin/jobs"),
  adminAssign: (jobId: string, contractorId: string) =>
    authReq("/admin/assign-tradie", { method: "POST", body: JSON.stringify({ jobId, contractorId }) }),
  adminForceStatus: (jobId: string, status: string, note?: string) =>
    authReq("/admin/update-status", { method: "POST", body: JSON.stringify({ jobId, status, note }) }),
  adminUnstick: () =>
    authReq("/admin/unstick", { method: "POST", body: JSON.stringify({}) }),
  getAdminActions: () => authReq("/admin/actions"),
  generateContractorInviteLinks: (emails: string[]) =>
    authReq("/admin/contractor-invite-links", { method: "POST", body: JSON.stringify({ emails }) }),
  getContractorInviteLinks: () => authReq("/admin/contractor-invite-links"),
  trackInviteLinkClick: (refCode: string) =>
    request(`/contractor-invite-links/${refCode}/click`, { method: "POST" }),
  getAdminPayments: () => authReq("/admin/payments"),
  getPricingEvents: () => authReq("/admin/pricing-events"),
  getAdminContractors: (params?: { 
    page?: number; 
    pageSize?: number; 
    status?: string; 
    search?: string;
    trade?: string;
    state?: string;
    suburb?: string;
    postcode?: string;
    businessType?: string;
    isVerified?: boolean;
    isLicensed?: boolean;
    isContactVerified?: boolean;
    activeOnly?: boolean;
    minRating?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.page) q.append("page", String(params.page));
    if (params?.pageSize) q.append("pageSize", String(params.pageSize));
    if (params?.status) q.append("status", params.status);
    if (params?.search) q.append("search", params.search);
    if (params?.trade) q.append("trade", params.trade);
    if (params?.state) q.append("state", params.state);
    if (params?.suburb) q.append("suburb", params.suburb);
    if (params?.postcode) q.append("postcode", params.postcode);
    if (params?.businessType) q.append("businessType", params.businessType);
    if (params?.isVerified !== undefined) q.append("isVerified", String(params.isVerified));
    if (params?.isLicensed !== undefined) q.append("isLicensed", String(params.isLicensed));
    if (params?.isContactVerified !== undefined) q.append("isContactVerified", String(params.isContactVerified));
    if (params?.activeOnly !== undefined) q.append("activeOnly", String(params.activeOnly));
    if (params?.minRating) q.append("minRating", String(params.minRating));
    const query = q.toString() ? `?${q.toString()}` : "";
    return authReq(`/admin/contractors${query}`);
  },
  exportAdminContractors: () => authReq("/admin/contractors/export"),
  getAdminContractor: (id: string) => authReq(`/admin/contractors/${id}`),
  deleteContractor: (id: string) =>
    authReq(`/admin/contractors/${id}`, { method: "DELETE" }),

  // ── Blogs ──
  getBlogs: () => request("/blogs"),
  getBlog: (slug: string) => request(`/blogs/${slug}`),
  getAdminBlogs: () => authReq("/admin/blogs"),
  createBlog: (body: unknown) =>
    authReq("/admin/blogs", { method: "POST", body: JSON.stringify(body) }),
  updateBlog: (id: string, body: unknown) =>
    authReq(`/admin/blogs/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteBlog: (id: string) =>
    authReq(`/admin/blogs/${id}`, { method: "DELETE" }),

  // ── Analytics ──
  getSupplyAnalytics: () => request("/analytics/supply"),
  getFunnelAnalytics: () => request("/analytics/funnel"),
  getRetentionAnalytics: () => request("/analytics/retention"),
  getUserAnalytics: () => authReq("/analytics/user"),
  getAnalyticsOverview: () => authReq("/analytics/overview"),

  // ── Command Center (proposal approval foundation) ──
  getAgents: () => authReq("/command-center/agents"),
  runAgent: (key: string, params?: unknown) =>
    authReq(`/command-center/agents/${key}/run`, { method: "POST", body: JSON.stringify({ params }) }),
  getProposals: (status?: string) =>
    authReq(`/command-center/proposals${status ? `?status=${status}` : ""}`),
  approveProposal: (id: string, note?: string, phoneNumber?: string) =>
    authReq(`/command-center/proposals/${id}/approve`, { method: "POST", body: JSON.stringify({ note, phoneNumber }) }),
  rejectProposal: (id: string, note?: string) =>
    authReq(`/command-center/proposals/${id}/reject`, { method: "POST", body: JSON.stringify({ note }) }),
  getActivityLog: (limit = 100) => authReq(`/command-center/log?limit=${limit}`),
  getAgentControls: () => authReq("/command-center/controls"),
  toggleAgentControl: (agentKey: string, field: "enabled" | "shadow") =>
    authReq(`/command-center/controls/${agentKey}/toggle`, { method: "POST", body: JSON.stringify({ field }) }),
  getFounderScore: () => authReq("/command-center/founder"),
  getUsageLedger: (days?: number) => authReq(`/command-center/usage/ledger${days ? `?days=${days}` : ""}`),

  // ── Email Campaign Agent (Sofia) - contact lists & campaigns ──
  getContactLists: () => authReq("/command-center/email-campaign/contact-lists"),
  createContactList: (name: string, description?: string) =>
    authReq("/command-center/email-campaign/contact-lists", { method: "POST", body: JSON.stringify({ name, description }) }),
  getContactListContacts: (listId: string) => authReq(`/command-center/email-campaign/contact-lists/${listId}/contacts`),
  addContacts: (listId: string, text: string) =>
    authReq(`/command-center/email-campaign/contact-lists/${listId}/contacts`, { method: "POST", body: JSON.stringify({ text }) }),
  removeContact: (listId: string, contactId: string) =>
    authReq(`/command-center/email-campaign/contact-lists/${listId}/contacts/${contactId}`, { method: "DELETE" }),
  getEmailCampaigns: () => authReq("/command-center/email-campaign/campaigns"),
  pauseEmailCampaign: (id: string) => authReq(`/command-center/email-campaign/campaigns/${id}/pause`, { method: "POST" }),
  resumeEmailCampaign: (id: string) => authReq(`/command-center/email-campaign/campaigns/${id}/resume`, { method: "POST" }),
  cancelEmailCampaign: (id: string) => authReq(`/command-center/email-campaign/campaigns/${id}/cancel`, { method: "POST" }),

  // ── Scout / Data Collection Agent - Lead Vault ──
  getScrapeJobs: () => authReq("/command-center/data-collection/jobs"),
  getScrapeJob: (id: string) => authReq(`/command-center/data-collection/jobs/${id}`),
  getScrapeSourceHealth: () => authReq("/command-center/data-collection/source-health"),
  getScrapeCountryCoverage: () => authReq("/command-center/data-collection/country-coverage"),
  scrapeJobAction: (id: string, action: "pause" | "resume" | "cancel" | "retry") =>
    authReq(`/command-center/data-collection/jobs/${id}/${action}`, { method: "POST" }),
  transferScrapeToEmail: (recordIds: string[], contactListId: string) =>
    authReq("/command-center/data-collection/transfer/email", { method: "POST", body: JSON.stringify({ recordIds, contactListId }) }),
  transferScrapeToCalling: (recordIds: string[]) =>
    authReq("/command-center/data-collection/transfer/calling", { method: "POST", body: JSON.stringify({ recordIds }) }),

  // ── Integrations ──
  getGoogleAnalyticsStatus: () => authReq("/integrations/google-analytics/status"),
  getGoogleAnalyticsConnectUrl: () => authReq("/integrations/google-analytics/connect"),
  disconnectGoogleAnalytics: () =>
    authReq("/integrations/google-analytics/disconnect", { method: "POST" }),
  getZohoStatus: () => authReq("/integrations/zoho/status"),
  connectZohoWithCode: (code: string) =>
    authReq("/integrations/zoho/connect", { method: "POST", body: JSON.stringify({ code }) }),
  disconnectZoho: () => authReq("/integrations/zoho/disconnect", { method: "POST" }),
  getMetaStatus: () => authReq("/integrations/meta/status"),
  getMetaConnectUrl: () => authReq("/integrations/meta/connect"),
  disconnectMeta: () => authReq("/integrations/meta/disconnect", { method: "POST" }),
  getDiscordStatus: () => authReq("/integrations/discord/status"),
  connectDiscordWithWebhook: (webhookUrl: string) =>
    authReq("/integrations/discord/connect", { method: "POST", body: JSON.stringify({ webhookUrl }) }),
  disconnectDiscord: () => authReq("/integrations/discord/disconnect", { method: "POST" }),

  // ── CRM / Leads ──
  createLead: (body: unknown) =>
    request("/tradie-leads", { method: "POST", body: JSON.stringify(body) }),
  getLeads: () => request("/tradie-leads"),
  updateLead: (id: string, body: unknown) =>
    request(`/tradie-leads/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  moveLead: (id: string, status: string, notes?: string) =>
    request(`/tradie-leads/${id}/move`, { method: "POST", body: JSON.stringify({ status, notes }) }),

  // ── Admin Contractor Force Verify ──
  adminVerifyEmail: (contractorId: string, emailId: string) =>
    authReq(`/contractors/${contractorId}/verify-email/${emailId}`, { method: "POST" }),
  adminVerifyPhone: (contractorId: string, phoneId: string) =>
    authReq(`/contractors/${contractorId}/verify-phone/${phoneId}`, { method: "POST" }),

  // ── Payments ──
  createCheckoutSession: (jobId: string) =>
    authReq("/payments/create-checkout-session", { method: "POST", body: JSON.stringify({ jobId }) }),
  verifyPayment: (sessionId: string) =>
    authReq(`/payments/verify?sessionId=${sessionId}`),

  // ── File Upload ──
  uploadFiles: (files: File[]) => {
    const formData = new FormData();
    files.forEach(f => formData.append("files", f));
    return fetch(`${API_BASE}/upload`, {
      method: "POST",
      body: formData,
      headers: {
        ...getAuthHeader(),
      }
    }).then(res => {
      if (!res.ok) throw new Error("Upload failed");
      return res.json() as Promise<{ urls: string[] }>;
    });
  },
  uploadChatFiles: (files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    return fetch(`${API_BASE}/chat-upload`, {
      method: "POST",
      body: formData,
      headers: getAuthHeader(),
    }).then(async (res) => {
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.error || "Attachment upload failed");
      }
      return res.json() as Promise<{ urls: string[] }>;
    });
  },

  // ── Contractor SMTP Invitation Flow ──
  inviteBulkContractors: () =>
    authReq("/admin/contractors/invite-bulk", { method: "POST" }),
  getContractorInviteInfo: (id: string) =>
    request(`/contractor/${id}/invite-info`),
  requestContractorInviteCode: (id: string) =>
    request(`/contractor/${id}/request-invite-code`, { method: "POST" }),
  setContractorPassword: (id: string, password: string, code: string) =>
    request(`/contractor/${id}/set-password`, { method: "POST", body: JSON.stringify({ password, code }) }),

  // ── Contact ──
  submitContactForm: (body: { name: string; email: string; subject?: string; message: string }) =>
    request("/contact", { method: "POST", body: JSON.stringify(body) }),
};

